import React, { useState, useEffect, useCallback, ChangeEvent, FormEvent } from 'react';
import axios from 'axios';
import { HeroImageDropzone } from '../components/HeroImageDropzone';
import { festivalsAPI, handleApiError } from '../services/api';
import { showSuccess, showError } from '../utils/toast';
import type {
  Festival,
  FestivalCreatePayload,
  FestivalUpdatePayload,
  FestivalStatus,
} from '../types';
import { FESTIVAL_STATUS_VALUES } from '../types/festival.types';
import './Festivals.css';

export type FestivalFormMode = 'create' | 'edit';

interface FestivalFormProps {
  mode: FestivalFormMode;
  festivalId?: string; // required when mode === 'edit'
  onCancel: () => void;
  onSaved: (festival: Festival) => void;
}

interface FormState {
  slug: string;
  name: string;
  name_en: string;
  description: string;
  description_en: string;
  hero_image_url: string;
  venue_name: string;
  venue_city: string;
  venue_lat: string; // kept as string so empty/decimal typing works cleanly
  venue_lng: string;
  geofence_radius_km: string;
  starts_at: string; // datetime-local value (no tz suffix)
  ends_at: string;
  status: FestivalStatus;
}

const EMPTY_FORM: FormState = {
  slug: '',
  name: '',
  name_en: '',
  description: '',
  description_en: '',
  hero_image_url: '',
  venue_name: '',
  venue_city: '',
  venue_lat: '',
  venue_lng: '',
  geofence_radius_km: '15',
  starts_at: '',
  ends_at: '',
  status: 'DRAFT',
};

// ISO (with offset) → "YYYY-MM-DDTHH:mm" for native datetime-local inputs.
const isoToLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

// datetime-local has no timezone — treat as local, convert to ISO with offset.
const localInputToIso = (local: string): string => {
  if (!local) return '';
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
};

// FastAPI 422 returns { detail: [{loc, msg, type}, ...] } — render readably,
// and keep per-field errors so we can show them inline.
interface FieldErrors {
  [field: string]: string;
}

interface ParsedValidation {
  message: string;
  fields: FieldErrors;
}

const parseValidationError = (err: unknown): ParsedValidation | null => {
  if (!axios.isAxiosError(err) || !err.response) return null;
  const status = err.response.status;
  const data = err.response.data as
    | { detail?: unknown; message?: string }
    | undefined;
  if (status === 422 && Array.isArray(data?.detail)) {
    const fields: FieldErrors = {};
    const lines: string[] = [];
    for (const item of data.detail as Array<{ loc?: unknown[]; msg?: string }>) {
      const loc = Array.isArray(item.loc) ? item.loc : [];
      // loc usually starts with "body", then the field name(s).
      const fieldName = loc.filter((p) => p !== 'body').join('.') || 'form';
      const msg = item.msg || 'Invalid value';
      if (!fields[fieldName]) fields[fieldName] = msg;
      lines.push(`${fieldName}: ${msg}`);
    }
    return { message: lines.join('; '), fields };
  }
  return null;
};

const FestivalForm: React.FC<FestivalFormProps> = ({
  mode,
  festivalId,
  onCancel,
  onSaved,
}) => {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState<boolean>(mode === 'edit');
  const [saving, setSaving] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadFestival = useCallback(async (): Promise<void> => {
    if (mode !== 'edit' || !festivalId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await festivalsAPI.getFestival(festivalId);
      const f = res.data;
      setForm({
        slug: f.slug,
        name: f.name,
        name_en: f.name_en ?? '',
        description: f.description ?? '',
        description_en: f.description_en ?? '',
        hero_image_url: f.hero_image_url ?? '',
        venue_name: f.venue_name ?? '',
        venue_city: f.venue_city ?? '',
        venue_lat: String(f.venue_lat),
        venue_lng: String(f.venue_lng),
        geofence_radius_km: String(f.geofence_radius_km),
        starts_at: isoToLocalInput(f.starts_at),
        ends_at: isoToLocalInput(f.ends_at),
        status: f.status,
      });
    } catch (err) {
      setLoadError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [mode, festivalId]);

  useEffect(() => {
    loadFestival();
  }, [loadFestival]);

  const setField = <K extends keyof FormState>(key: K) => (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value } as FormState));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key as string];
        return next;
      });
    }
  };

  // Client-side gate — covers the easy mistakes so we don't waste a round-trip.
  // Backend remains the source of truth for the full ruleset (slug regex, lat/lng ranges, etc).
  const validateLocal = (): FieldErrors => {
    const errs: FieldErrors = {};
    if (!form.slug.trim()) errs.slug = 'Slug е задължителен';
    if (!form.name.trim()) errs.name = 'Името е задължително';
    if (!form.venue_lat.trim()) errs.venue_lat = 'Ширина е задължителна';
    if (!form.venue_lng.trim()) errs.venue_lng = 'Дължина е задължителна';
    // venue_city is required when publishing — backend also enforces this.
    if (form.status === 'PUBLISHED' && !form.venue_city.trim()) {
      errs.venue_city = 'Град е задължителен при публикуване';
    }
    if (!form.starts_at) errs.starts_at = 'Начална дата е задължителна';
    if (!form.ends_at) errs.ends_at = 'Крайна дата е задължителна';
    if (form.starts_at && form.ends_at && form.ends_at <= form.starts_at) {
      errs.ends_at = 'Крайната дата трябва да е след началната';
    }
    const radius = parseFloat(form.geofence_radius_km);
    if (Number.isNaN(radius) || radius <= 0) {
      errs.geofence_radius_km = 'Радиусът трябва да е > 0';
    }
    return errs;
  };

  const buildCreatePayload = (): FestivalCreatePayload => ({
    slug: form.slug.trim(),
    name: form.name.trim(),
    name_en: form.name_en.trim() || null,
    description: form.description.trim() || null,
    description_en: form.description_en.trim() || null,
    hero_image_url: form.hero_image_url.trim() || null,
    venue_name: form.venue_name.trim() || null,
    venue_city: form.venue_city.trim() || null,
    venue_lat: parseFloat(form.venue_lat),
    venue_lng: parseFloat(form.venue_lng),
    geofence_radius_km: parseFloat(form.geofence_radius_km),
    starts_at: localInputToIso(form.starts_at),
    ends_at: localInputToIso(form.ends_at),
    status: form.status,
  });

  // Update payload sends the full form — backend treats all fields as optional
  // but we want PUT semantics (full replacement) consistent with the spec.
  const buildUpdatePayload = (): FestivalUpdatePayload => buildCreatePayload();

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const localErrs = validateLocal();
    if (Object.keys(localErrs).length > 0) {
      setFieldErrors(localErrs);
      showError('Моля поправете грешките във формуляра');
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      let saved: Festival;
      if (mode === 'create') {
        const res = await festivalsAPI.createFestival(buildCreatePayload());
        saved = res.data;
        showSuccess(`Фестивал „${saved.name}" е създаден`);
      } else if (festivalId) {
        const res = await festivalsAPI.updateFestival(festivalId, buildUpdatePayload());
        saved = res.data;
        showSuccess(`Фестивал „${saved.name}" е обновен`);
      } else {
        showError('Липсва ID на фестивала');
        return;
      }
      onSaved(saved);
    } catch (err) {
      // 409 → slug conflict; 422 → field validation; everything else → generic.
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setFieldErrors({ slug: 'Този slug вече се използва' });
        showError('Slug вече се използва');
      } else {
        const parsed = parseValidationError(err);
        if (parsed) {
          setFieldErrors(parsed.fields);
          showError(`Невалидни данни: ${parsed.message}`);
        } else {
          showError('Грешка при запазване: ' + handleApiError(err));
        }
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="festival-form">
        <p>Зареждане…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="festivals-error">
        <i className="fas fa-exclamation-triangle"></i>
        {loadError}
      </div>
    );
  }

  return (
    <form className="festival-form" onSubmit={handleSubmit} noValidate>
      <h2>{mode === 'create' ? 'Нов фестивал' : 'Редактиране на фестивал'}</h2>

      <div className="form-grid">
        <div>
          <label htmlFor="festival-name">
            Име <span className="required">*</span>
          </label>
          <input
            id="festival-name"
            type="text"
            value={form.name}
            onChange={setField('name')}
            required
          />
          {fieldErrors.name && <div className="field-error">{fieldErrors.name}</div>}
        </div>

        <div>
          <label htmlFor="festival-name-en">Име (English)</label>
          <input
            id="festival-name-en"
            type="text"
            value={form.name_en}
            onChange={setField('name_en')}
          />
          {fieldErrors.name_en && <div className="field-error">{fieldErrors.name_en}</div>}
        </div>

        <div>
          <label htmlFor="festival-slug">
            Slug <span className="required">*</span>
          </label>
          <input
            id="festival-slug"
            type="text"
            value={form.slug}
            onChange={setField('slug')}
            placeholder="spirit-of-burgas-2026"
            required
          />
          <div className="field-hint">lowercase, цифри и тирета</div>
          {fieldErrors.slug && <div className="field-error">{fieldErrors.slug}</div>}
        </div>

        <div>
          <label htmlFor="festival-status">Статус</label>
          <select
            id="festival-status"
            value={form.status}
            onChange={setField('status')}
          >
            {FESTIVAL_STATUS_VALUES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {fieldErrors.status && <div className="field-error">{fieldErrors.status}</div>}
        </div>

        <div className="full">
          <label htmlFor="festival-description">Описание</label>
          <textarea
            id="festival-description"
            value={form.description}
            onChange={setField('description')}
          />
          {fieldErrors.description && (
            <div className="field-error">{fieldErrors.description}</div>
          )}
        </div>

        <div className="full">
          <label htmlFor="festival-description-en">Описание (English)</label>
          <textarea
            id="festival-description-en"
            value={form.description_en}
            onChange={setField('description_en')}
          />
        </div>

        <div className="full">
          <label>Банер</label>
          <HeroImageDropzone
            festivalId={mode === 'edit' ? festivalId : null}
            currentUrl={form.hero_image_url || null}
            onUploaded={(newUrl) => {
              setForm((prev) => ({ ...prev, hero_image_url: newUrl }));
              setFieldErrors((prev) => {
                const next = { ...prev };
                delete next.hero_image_url;
                return next;
              });
              showSuccess('Банерът е качен');
            }}
          />
          {/* Manual URL override (for admins who want to point at an external
              CDN instead of uploading). Kept compact below the dropzone. */}
          <div style={{ marginTop: 12 }}>
            <label htmlFor="festival-hero" style={{ fontSize: 12, opacity: 0.7 }}>
              Или въведи URL ръчно
            </label>
            <input
              id="festival-hero"
              type="url"
              value={form.hero_image_url}
              onChange={setField('hero_image_url')}
              placeholder="https://..."
            />
            {fieldErrors.hero_image_url && (
              <div className="field-error">{fieldErrors.hero_image_url}</div>
            )}
          </div>
        </div>

        <div className="full">
          <label htmlFor="festival-venue-name">Място</label>
          <input
            id="festival-venue-name"
            type="text"
            value={form.venue_name}
            onChange={setField('venue_name')}
            placeholder="Studio 21"
          />
        </div>

        <div className="full">
          <label htmlFor="festival-venue-city">
            Най-близък град{' '}
            {form.status === 'PUBLISHED' && (
              <span className="required">*</span>
            )}
          </label>
          <input
            id="festival-venue-city"
            type="text"
            value={form.venue_city}
            onChange={setField('venue_city')}
            placeholder="напр. София, Бургас, Варна"
          />
          <div className="field-hint">
            Маршрутизиращият двигател знае този град. Шофьорите, които изберат фестивала,
            получават маршрут до тук.
          </div>
          {fieldErrors.venue_city && (
            <div className="field-error">{fieldErrors.venue_city}</div>
          )}
        </div>

        <div>
          <label htmlFor="festival-venue-lat">
            Ширина (lat) <span className="required">*</span>
          </label>
          <input
            id="festival-venue-lat"
            type="number"
            step="any"
            value={form.venue_lat}
            onChange={setField('venue_lat')}
            placeholder="Paste from Google Maps right-click"
            required
          />
          {fieldErrors.venue_lat && (
            <div className="field-error">{fieldErrors.venue_lat}</div>
          )}
        </div>

        <div>
          <label htmlFor="festival-venue-lng">
            Дължина (lng) <span className="required">*</span>
          </label>
          <input
            id="festival-venue-lng"
            type="number"
            step="any"
            value={form.venue_lng}
            onChange={setField('venue_lng')}
            placeholder="Paste from Google Maps right-click"
            required
          />
          {fieldErrors.venue_lng && (
            <div className="field-error">{fieldErrors.venue_lng}</div>
          )}
        </div>

        <div>
          <label htmlFor="festival-radius">Радиус на геозона (km)</label>
          <input
            id="festival-radius"
            type="number"
            step="0.01"
            min="0.01"
            value={form.geofence_radius_km}
            onChange={setField('geofence_radius_km')}
          />
          {fieldErrors.geofence_radius_km && (
            <div className="field-error">{fieldErrors.geofence_radius_km}</div>
          )}
        </div>

        <div>
          <label htmlFor="festival-starts">
            Начална дата <span className="required">*</span>
          </label>
          <input
            id="festival-starts"
            type="datetime-local"
            value={form.starts_at}
            onChange={setField('starts_at')}
            required
          />
          {fieldErrors.starts_at && (
            <div className="field-error">{fieldErrors.starts_at}</div>
          )}
        </div>

        <div>
          <label htmlFor="festival-ends">
            Крайна дата <span className="required">*</span>
          </label>
          <input
            id="festival-ends"
            type="datetime-local"
            value={form.ends_at}
            onChange={setField('ends_at')}
            required
          />
          {fieldErrors.ends_at && <div className="field-error">{fieldErrors.ends_at}</div>}
        </div>
      </div>

      <div className="festival-form-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={onCancel}
          disabled={saving}
        >
          Отказ
        </button>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving
            ? 'Запазване…'
            : mode === 'create'
              ? 'Създай'
              : 'Запази промените'}
        </button>
      </div>
    </form>
  );
};

export default FestivalForm;
